import { Person } from '@/api/entities/people/types'
import { Timestamps } from '@/api/global/types/timestamps'
import { IdMap } from '@/legacy/types/api/store'
import { Address } from '@/legacy/types/entities/addresses'
import { MapEntityPhoneNumber } from '@/legacy/types/entities/phoneNumbers'
import { Nullable } from '@/libs/typing/base'

// CONSTANTS ----------------

export const SYSTEM_USER_UUID = '00000000-2499-410d-85de-8baf9b92c245'

// TYPES --------------------
export type StaffMap = IdMap<Entity>

export type MapEntitiesLicensedState = {
  entityId: string
  licensedStates: string[]
} & Timestamps

export type Label = {
  entityId: string
  labelId: string
  labelRefId: string
} & Timestamps

export type LabelReference = {
  labelRefId: string
  description: string
} & Timestamps

export type ApiEntityRole = {
  entityId: string
  role: string
} & Timestamps

export type Entity = {
  entityId: string
  email: string
  isTest?: boolean
  person?: Person
  phoneNumbers?: MapEntityPhoneNumber[]
  licensedStates?: MapEntitiesLicensedState
  addresses?: Address[]
  roles?: ApiEntityRole[]
  userRoles?: string[]
} & Timestamps

export type ContactEntityWithRelationship = {
  relationshipToPatient: Nullable<string>
} & Entity

export type Email = {
  email: string
}

export type CreateEntityResponse = Entity

export type UserForm = {
  firstName: string
  lastName: string
  email: string
  role: EntityRole
}
// ENUMS --------------------
export enum EntityRoleSelectType {
  Dropdown = 'dropdown',
  PVDropdown = 'PVDropdown',
  Radio = 'radio',
}

export enum EntityRole {
  TECH_TEAM = 'tech_team',
  ADMIN = 'admin',
  CLINICAL = 'clinical',
  CLINICAL__CARE_PARTNER = 'clinical__care_partner',
  CLINICAL__NURSE = 'clinical__nurse',
  CLINICAL__NURSE_TEAM_LEAD = 'clinical__nurse_team_lead',
  CLINICAL__ENROLLMENT_SPECIALIST = 'clinical__enrollment',
  CLINICAL__ENROLLMENT_SPECIALIST_LEAD = 'clinical__enrollment_lead',
  CLINICAL__CARE_POD_LEAD = 'clinical__care_pod_lead',
  CLINICAL__ADMIN_ASSISTANT = 'clinical__admin_assistant',
  CLINICAL__MEDICAL_DIRECTOR = 'clinical__medical_director',
  CLINICAL__TIERING_NP = 'clinical__tiering_np',
  CLINICAL__LIAISON = 'clinical__liaison',
  CLINICAL__THYME_CARE_MEDICAL_AA = 'clinical__thyme_care_medical_aa',
  CLINICAL__SOCIAL_WORKER = 'clinical__social_worker',
  CLINICAL__NURSE_PRACTITIONER = 'clinical__nurse_practitioner',
  STAFF = 'staff',
  SYSTEM = 'system',
  AUTOMATION = 'automation',
  PATIENT = 'patient',
  PROVIDER = 'provider',
  CONTACT = 'contact',
  CARE_TEAM_LEADERSHIP = 'care_team_leadership',
  SPECIALIZATION__MEDICAL_DIRECTOR_PALLIATIVE_CARE = 'specialization__medical_director_palliative_care',
  SPECIALIZATION__MEDICAL_DIRECTOR_ONCOLOGY = 'specialization__medical_director_oncology',
  SPECIALIZATION__ONBOARDING_RN = 'specialization__onboarding_rn',
  SPECIALIZATION__ONBOARDING_CP = 'specialization__onboarding_cp',
  SPECIALIZATION__FLOAT_RN = 'specialization__float_rn',
  SPECIALIZATION__FLOAT_CP = 'specialization__float_cp',
  SPECIALIZATION__MEDICAL_RECORDS_AA = 'specialization__medical_records_aa',
  SPECIALIZATION__TRIAGE_RN = 'specialization__triage_rn',
  SPECIALIZATION__TRIAGE_NP = 'specialization__triage_np',
  SPECIALIZATION__TRIAGE_SW = 'specialization__triage_sw',
  SPECIALIZATION__TRIAGE_CP = 'specialization__triage_cp',
  SPECIALIZATION__SERIOUS_ILLNESS_CONVERSATION_RN = 'specialization__serious_illness_conversation_rn',
  SPECIALIZATION__SERIOUS_ILLNESS_CONVERSATION_CP = 'specialization__serious_illness_conversation_cp',
  SPECIALIZATION__BEHAVIORAL_HEALTH_LEAD = 'specialization__behavioral_health_lead',
  SPECIALIZATION__BEHAVIORAL_HEALTH_CP = 'specialization__behavioral_health_cp',
  SPECIALIZATION__BEHAVIORAL_HEALTH_RN = 'specialization__behavioral_health_rn',
  SPECIALIZATION__BEHAVIORAL_HEALTH_SW = 'specialization__behavioral_health_sw',
  SPECIALIZATION__COMPLEX_TEAM_LEAD = 'specialization__complex_team_lead',
  SPECIALIZATION__ADVANCED_SOCIAL_NEEDS_CP = 'specialization__advanced_social_needs_cp',
  SPECIALIZATION__ADVANCED_SOCIAL_NEEDS_SW = 'specialization__advanced_social_needs_sw',
  SPECIALIZATION__ADVANCED_SOCIAL_NEEDS_RN = 'specialization__advanced_social_needs_rn',
  SPECIALIZATION__SITE_OF_SERVICE_SHIFT_CP = 'specialization__site_of_service_shift_cp',
  SPECIALIZATION__SITE_OF_SERVICE_SHIFT_RN = 'specialization__site_of_service_shift_rn',
  SPECIALIZATION__TRANSITIONS_OF_CARE_CP = 'specialization__transitions_of_care_cp',
  SPECIALIZATION__TRANSITIONS_OF_CARE_RN = 'specialization__transitions_of_care_rn',
  SPECIALIZATION__TRANSITIONS_OF_CARE_AA = 'specialization__transitions_of_care_aa',
  SPECIALIZATION__TCM_COB_RN = 'specialization__tcm_cob_rn',
  SPECIALIZATION__MAILER_AA = 'specialization__mailer_aa',
  SPECIALIZATION__PALLIATIVE_CARE_NP = 'specialization__palliative_care_np',
  SPECIALIZATION__HWV_NP = 'specialization__hwv_np',
  SPECIALIZATION__COMMUNITY_CP = 'specialization__community_cp',
  SPECIALIZATION__PRACTICE_COORDINATOR_TEAM_LEAD = 'specialization__practice_coordinator_team_lead',
  SPECIALIZATION__TRANSITIONS_OF_CARE_ES = 'specialization__transitions_of_care_es',
  SPECIALIZATION__REFERRAL_ES = 'specialization__referral_es',
  NURSE_PRACTITIONER_TEAM_LEAD = 'nurse_practitioner_team_lead',
  // Care Team support roles
  QUALITY_TRAINING_ENABLEMENT = 'quality_training_enablement',
  CONTENT_MANAGER = 'content_manager',
  TCOP_PARTNERSHIP_OPS = 'tcop_partnership_ops',
  TCOP_NP_LIAISON = 'tcop_np_liaison',
  PRACTICE_COORDINATOR = 'practice_coordinator',
  PRACTICE_ADMINISTRATOR = 'practice_administrator',
  ON_DEMAND_MEMBER_SUPPORT = 'on_demand_member_support',
  // Enrollment Ops support roles
  ENROLLMENT_OPS_COORDINATOR = 'enrollment_ops_coordinator',
}

export enum USState {
  AL = 'Alabama',
  AK = 'Alaska',
  AZ = 'Arizona',
  AR = 'Arkansas',
  CA = 'California',
  CO = 'Colorado',
  CT = 'Connecticut',
  DE = 'Delaware',
  DC = 'District of Columbia',
  FL = 'Florida',
  GA = 'Georgia',
  HI = 'Hawaii',
  ID = 'Idaho',
  IL = 'Illinois',
  IN = 'Indiana',
  IA = 'Iowa',
  KS = 'Kansas',
  KY = 'Kentucky',
  LA = 'Louisiana',
  ME = 'Maine',
  MD = 'Maryland',
  MA = 'Massachusetts',
  MI = 'Michigan',
  MN = 'Minnesota',
  MS = 'Mississippi',
  MO = 'Missouri',
  MT = 'Montana',
  NE = 'Nebraska',
  NV = 'Nevada',
  NH = 'New Hampshire',
  NJ = 'New Jersey',
  NM = 'New Mexico',
  NY = 'New York',
  NC = 'North Carolina',
  ND = 'North Dakota',
  OH = 'Ohio',
  OK = 'Oklahoma',
  OR = 'Oregon',
  PA = 'Pennsylvania',
  RI = 'Rhode Island',
  SC = 'South Carolina',
  SD = 'South Dakota',
  TN = 'Tennessee',
  TX = 'Texas',
  UT = 'Utah',
  VT = 'Vermont',
  VA = 'Virginia',
  WA = 'Washington',
  WV = 'West Virginia',
  WI = 'Wisconsin',
  WY = 'Wyoming',
}

export const nurseLicensureCompactStates = {
  label: 'Nurse Licensure Compact (NLC)',
  value: 'NLC',
}

// NLC member states (from https://www.ncsbn.org/public-files/NLC_Map.pdf)
// Last updated August 2026
export const NLC_MEMBER_STATES: string[] = [
  'AL',
  'AZ',
  'AR',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'ID',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  // 'MA', Massachusetts has enacted NLC but not implemented
  'MS',
  'MO',
  'MT',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NC',
  'ND',
  'OH',
  'OK',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]

export const createEntityFields = [
  'person',
  'phoneNumbers',
  'addresses',
] as const
export type CreateEntityRequest = Pick<
  Entity,
  (typeof createEntityFields)[number]
>
